import { useQuery } from "@apollo/client/react"
import Alert from "@mui/material/Alert"
import Card from "@mui/material/Card"
import Typography from "@mui/material/Typography"
import { DateTime } from "luxon"
import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"

import ACTIVITIES from "@/components/activities"
import ActivitySkeleton from "@/components/activities/skeleton"
import Pagination from "@/components/pagination"
import { useVenue } from "@/contexts/venue"
import {
  GetVenueActivitiesDocument,
  type GetVenueActivitiesQuery,
  type GetVenueActivitiesQueryVariables,
} from "@/graphql/graphql"
import i18n from "@/i18n"
import { usePanelLink } from "@/shared/hooks"

import Layout from "../../../components/layout"

interface DateSeparatorProps {
  date: string
}

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

const DateSeparator = ({ date }: DateSeparatorProps) => {
  const dateTime = DateTime.fromISO(date)
  const now = DateTime.now()
  const diffDays = Math.floor(now.diff(dateTime, "days").days)

  let displayText: string
  if (diffDays < 7) {
    displayText = capitalize(dateTime.toRelativeCalendar() || dateTime.toLocaleString(DateTime.DATE_FULL))
  } else {
    displayText = dateTime.toLocaleString(DateTime.DATE_FULL)
  }

  return (
    <div
      data-testid="panel-activity-log-date-separator"
      className="px-6 py-2 bg-gray-50 border-0 border-solid border-y border-gray-200"
    >
      <span className="text-lg font-medium text-gray-900">{displayText}</span>
    </div>
  )
}

const ActivityLog = () => {
  const { venue } = useVenue()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const navigate = useNavigate()
  const linkTo = usePanelLink()

  useEffect(() => {
    if (!venue.permissions?.viewActivityLog) {
      navigate(linkTo("settings"))
    }
  }, [venue.permissions?.viewActivityLog, navigate, linkTo])

  const { data, loading, error } = useQuery<GetVenueActivitiesQuery, GetVenueActivitiesQueryVariables>(
    GetVenueActivitiesDocument,
    {
      variables: {
        slug: venue.slug,
        page,
        perPage,
        includeRelated: true,
      },
    },
  )

  const activities = data?.venue?.activities?.collection || []
  const metadata = data?.venue?.activities?.metadata

  const activitiesWithDateSeparators = useMemo(() => {
    const result: Array<{ type: "date"; date: string } | { type: "activity"; activity: (typeof activities)[0] }> = []
    let lastDate: string | null = null

    for (const activity of activities) {
      const activityDate = DateTime.fromISO(activity.createdAt).startOf("day").toISO()
      if (activityDate !== lastDate) {
        result.push({ type: "date", date: activity.createdAt })
        lastDate = activityDate
      }
      result.push({ type: "activity", activity })
    }

    return result
  }, [activities])

  const renderActivity = (activity) => {
    const Component = ACTIVITIES[activity.key]

    if (Component) {
      return (
        <Component
          key={activity.id}
          venue={venue}
          activity={activity}
          className="border-0 border-solid border-t border-gray-100 px-6"
        />
      )
    }

    return (
      <div key={activity.id} className="p-4 border-b border-gray-100">
        <Typography variant="body2" className="text-gray-500">
          {i18n.t("app.activity_log.unknown_activity")}: {activity.key}
        </Typography>
      </div>
    )
  }

  const renderItem = (item: (typeof activitiesWithDateSeparators)[0], _index: number) => {
    if (item.type === "date") {
      return <DateSeparator key={`date-${item.date}`} date={item.date} />
    }
    return renderActivity(item.activity)
  }

  return (
    <Layout title={i18n.t("app.activity_log.header")} flex>
      <div data-testid="panel-activity-log-page" className="flex items-end justify-between w-full mb-4">
        <h1 className="text-3xl mt-3 mb-0 font-medium">{i18n.t("app.activity_log.header")}</h1>
      </div>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {i18n.t("app.activity_log.error")}
        </Alert>
      ) : null}

      <Card data-testid="panel-activity-log-card" variant="outlined" className="flex-1 flex flex-col">
        {loading && !data ? (
          <div className="flex flex-col gap-4 p-2">
            <ActivitySkeleton />
            <ActivitySkeleton />
            <ActivitySkeleton />
          </div>
        ) : activities.length === 0 ? (
          <div data-testid="panel-activity-log-empty" className="flex items-center justify-center flex-1 p-8">
            <Typography variant="body1" className="text-gray-500">
              {i18n.t("app.activity_log.empty")}
            </Typography>
          </div>
        ) : (
          <>
            <div data-testid="panel-activity-log-list" className="flex flex-col flex-1">
              {activitiesWithDateSeparators.map(renderItem)}
            </div>

            {metadata ? (
              <div data-testid="panel-activity-log-pagination" className="border-t border-gray-200">
                <Pagination
                  metadata={metadata}
                  page={page}
                  perPage={perPage}
                  onPageChange={setPage}
                  onPerPageChange={setPerPage}
                />
              </div>
            ) : null}
          </>
        )}
      </Card>
    </Layout>
  )
}

export default ActivityLog
